import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@app/auth/services/auth.service';
import { AiDesignRequestsService } from '@app/shared/services/ai_design_requests_service';

@Component({
  selector: 'app-special-register',
  templateUrl: './special_register.component.html',
  styleUrls: ['./special_register.component.scss']
})
export class SpecialRegisterComponent implements OnInit {
  form: FormGroup;
  error: string | null = null;
  loading = false;
  data: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private authService: AuthService,
    private aiDesignRequestsService: AiDesignRequestsService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const dataString = params['data_string'];
      try {
        const json = atob(dataString);
        this.data = JSON.parse(json);
        if (!this.data.email || !this.data.storeUrl) throw new Error('Missing required fields');
        // Map new fields for display and AI request
        this.data.tone = this.data.tone || '';
        this.data.style = this.data.style || '';
        this.data.context = this.data.context || '';
        this.data.emailType = this.data.emailType || 'Promotion';
        this.data.customHeroImage = this.data.customHeroImage !== undefined ? this.data.customHeroImage : true;
      } catch (e) {
        console.error('Failed to decode or parse registration data:', e);
        this.router.navigate(['/register']);
        return;
      }
      this.form = this.fb.group({
        password: ['', [Validators.required, Validators.minLength(8)]],
        agreeToTerms: [false, [Validators.requiredTrue]]
      });
    });
  }

  onSubmit() {
    if (this.form.invalid) return;
    this.loading = true;
    this.error = null;
    const password = this.form.get('password').value;
    const params = {
      email: this.data.email,
      store_url: this.data.storeUrl,
      password,
      password_confirmation: password
    };
    this.authService.register(params).subscribe({
      next: (response) => {
        if (response.ok) {
          // Now create the AI design request
          this.createAiDesignRequest();
        } else {
          this.loading = false;
          this.handleError(response);
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = 'An unexpected error has occurred.';
      }
    });
  }

  createAiDesignRequest() {
    const req = {
      status: 'pending',
      email_type: this.data.emailType,
      user_submitted_tone: `${this.data.tone}${this.data.style ? ' and ' + this.data.style : ''}`,
      custom_hero_image: this.data.customHeroImage,
      user_submitted_context: this.data.context,
      product_selection_type: 'top_products'
    };
    this.aiDesignRequestsService.create(req).subscribe({
      next: (response) => {
        this.loading = false;
        if (response && response.id) {
          this.router.navigate([`/email-design-generator/${response.id}/success`]);
        } else {
          // fallback if no id
          this.router.navigate(['/dashboard']);
        }
      },
      error: (err) => {
        this.loading = false;
        console.error('Failed to create AI design request:', err);
        this.router.navigate(['/dashboard']);
      }
    });
  }

  handleError(response: any) {
    if (response.error?.error === 'email already exists') {
      this.error = `Email already exists. <a href='/login'>Sign in</a> or <a href='/register'>register</a> with a different email.`;
    } else if (response.status === 403) {
      this.error = 'Registration invalid. Check your store website and try again.';
    } else if (response.status === 429) {
      this.error = 'Too many attempts. Try again later.';
    } else if (response.status === 401) {
      this.error = response.error.error;
    } else {
      this.error = 'An unknown error has occurred.';
    }
  }
} 